/**
 * Auth context — provides session state + auth actions to the React tree.
 *
 * @module apps/web/contexts/auth
 * @since W2.9
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  getSession,
  clearSession,
  isAuthenticated,
  login,
  logout as authLogout,
  type AuthSession,
  type TokenClaims,
} from '../lib/auth.js';

interface AuthContextValue {
  session:         AuthSession | null;
  claims:          TokenClaims | null;
  authenticated:   boolean;
  loading:         boolean;
  login:           (returnTo?: string, opts?: { prompt?: string }) => Promise<void>;
  logout:          () => Promise<void>;
  refresh:         () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setSession(getSession());
  }, []);

  useEffect(() => {
    const stored = getSession();

    if (stored && (!stored.claims.permissions || stored.claims.permissions.length === 0)) {
      // Stale session from before extraTokenClaims fix — JWT has no permissions
      // and the userinfo endpoint can't backfill them (resource-bound tokens are
      // rejected by oidc-provider's userinfo). Clear and let ProtectedRoute
      // redirect to login for a fresh JWT that embeds permissions.
      clearSession();
      setSession(null);
      setLoading(false);
    } else {
      setSession(stored);
      setLoading(false);
    }
  }, []);

  const handleLogout = useCallback(async () => {
    await authLogout();
    setSession(null);
  }, []);

  const value: AuthContextValue = {
    session,
    claims:        session?.claims ?? null,
    authenticated: isAuthenticated(),
    loading,
    login,
    logout:        handleLogout,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
